
import { mergeMap, distinct, filter, takeUntil } from 'rxjs/operators'
import * as isEmpty from 'lodash/isEmpty'
import { destroy, initialize, reset } from 'redux-form'
import { combineEpics } from 'redux-observable'
import { from, empty, of } from 'rxjs'
import * as toPath from 'lodash/toPath'
import {
  DELETE_PEER_GROUP_URL,
  FIND_PEER_GROUP_URL,
  GET_PEER_GROUP_RELATIONS_URL,
  GET_PEER_GROUP_TOTAL_TOTALS_URL,
  RANDOMIZE_PEER_GROUP_URL,
} from 'interfaces/ajax'
import { get, post } from 'helpers/observableAjaxHelpers'
import { FORM_CREATE_CUSTOM_PEER_GROUP, FORM_EDIT_CUSTOM_PEER_GROUP } from 'constants/forms'
import { FORM_FIELD_CHANGE } from 'actions/formActions'
import { closePeergroupFormActionCreator, EDIT_RELATION, hidePeergroupPopupActionCreator, showPeergroupEditFormActionCreator } from 'actions/sidebarActions'
import {
  appendPeergroupRelationActionCreator,
  appendPeergroupsTotalsActionCreator,
  deleteRelationActionCreator,
  FIND_PEER_GROUP,
  findPeergroupActionCreator,
  GET_PEER_GROUP_TOTALS,
  GET_PEER_GROUPS,
  RANDOMIZE_PEER_GROUP_RELATION,
  REQUEST_DELETE_RELATION,
  resetPeergroupFiltersActionCreator,
  selectRelationActionCreator,
  setFoundPeergroupActionCreator,
  setPeergroupFiltersActionCreator,
  setPeergroupsActionCreator,
  setRandomRelationActionCreator,
} from 'actions/peergroupActions'
import { Action } from 'actions'
import {
  buildFindPeergroupQuery,
  extractRelation,
  getDefaultRelation,
  getFormPeerFiltersInitialValues,
  getPeergroupFilterNames,
  isPeergroupForm,
  PEER_GROUP_FORM_FILTER_PREFIX,
  peergroupFiltersMap,
} from 'helpers/peergroupsHelpers'
import { Peergroup, PeergroupFilterName, PeergroupRelation, RawPeergroupRelation } from 'interfaces/peergroups'
import { Epic } from 'interfaces/middleware'
import { normalize } from 'helpers/normalizeHelpers'
import { selectedRelationIdSelector } from 'selectors/peergroupSelectors'
import { PEERGROUPS_DISCARD_TRIGGERS, PEERGROUPS_TOTALS_DISCARD_TRIGGERS } from 'helpers/discardTriggerHelpers'
import { byType } from 'rxOperators/byType'
import { formSubmitted } from 'rxOperators/formsLegacy'
import { mapSafe, mergeMapSafe } from 'rxOperators/mergeMapSafe'
import { toDiscardCache } from 'rxOperators/toDiscardCache'
import { catchSafe } from 'rxOperators/catchSafe'

// =========================================================================== //
//                           PEER GROUPS COMMON                                //
// =========================================================================== //

const getPeergroupsEpic: Epic = (action$) =>
  action$
    .pipe(byType(GET_PEER_GROUPS)).pipe(
      distinct(
      // simple action, use true as a key for all actions
        () => true,
        action$.pipe(toDiscardCache(PEERGROUPS_DISCARD_TRIGGERS)),
      ),
      mergeMap(() =>
        get(GET_PEER_GROUP_RELATIONS_URL)
          .pipe(mapSafe(({ response: { items }}) => {
            const peergroups: Peergroup[] = items.map((item) => item.peergroup)
            const relations: PeergroupRelation[] = items.map(extractRelation)

            const payload = {
              peergroups: normalize(peergroups, 'id'),
              relations: normalize(relations, 'id'),
            }

            return setPeergroupsActionCreator(payload)
          }))
      ))

const getPeergroupTotalsEpic: Epic = (action$) =>
  action$
    .pipe(byType(GET_PEER_GROUP_TOTALS)).pipe(
      distinct(
        ({ payload }) => JSON.stringify(payload),
        action$.pipe(toDiscardCache(PEERGROUPS_TOTALS_DISCARD_TRIGGERS)),
      ))
    .pipe(mergeMapSafe((action) => {
      const { date, ids } = action.payload
      const groupParams = {
        group: ids,
        date,
      }

      return get(GET_PEER_GROUP_TOTAL_TOTALS_URL, groupParams)
        .pipe(mapSafe(({ response: totals }) => {
          const totalsCollection = normalize(totals, 'id')
          return appendPeergroupsTotalsActionCreator(date, totalsCollection)
        }))
    }))

const randomizePeergroupEpic: Epic = (action$) =>
  action$
    .pipe(byType(RANDOMIZE_PEER_GROUP_RELATION)).pipe(
      mergeMap(() =>
        post(RANDOMIZE_PEER_GROUP_URL)
          .pipe(mergeMapSafe(({ response }) => from([
            setRandomRelationActionCreator(response),
            selectRelationActionCreator(response.id),
          ])))
      ))

const deleteRelationEpic: Epic = (action$, store) =>
  action$
    .pipe(byType(REQUEST_DELETE_RELATION)).pipe(
      mergeMap(({ payload: relationId }) =>
        post({ url: DELETE_PEER_GROUP_URL, params: { id: relationId }})
          .pipe(catchSafe()).pipe(
            mergeMap(() => {
              const state = store.getState()
              const actions: Action[] = []

              const selectedId = selectedRelationIdSelector(state)
              if (selectedId === relationId) {
                const defaultRelation = getDefaultRelation(state.data.peergroups.relations)
                if (defaultRelation) {
                  actions.push(selectRelationActionCreator(defaultRelation.id))
                }
              }

              actions.push(deleteRelationActionCreator(relationId))

              return from(actions)
            }))
      ))

// =========================================================================== //
//                             PEER GROUPS FORM                                //
// =========================================================================== //

const findPeergroupEpic: Epic = (action$) =>
  action$
    .pipe(byType(FIND_PEER_GROUP)).pipe(
      mergeMap((action) =>
        post(FIND_PEER_GROUP_URL, { body: action.payload }).pipe(
          takeUntil(
            action$.ofType(FIND_PEER_GROUP)
          ))
          .pipe(mapSafe(({ response }) => setFoundPeergroupActionCreator(response)))
      ))

// side-effects of changing peer group form fields
const handleChangePeergroupForm: Epic = (action$, store) =>
  action$
    .pipe(byType(FORM_FIELD_CHANGE)).pipe(
      filter((a) => isPeergroupForm(a.meta.form)),
      mergeMap((action) => {
        const { field, form } = action.meta
        const path = toPath(field)
        const filterName = path[0] === PEER_GROUP_FORM_FILTER_PREFIX
          ? field.split('.')[1] as PeergroupFilterName
          : null

        if (!filterName) {
          return empty()
        }

        const value = action.payload
        const state = store.getState()
        const isSelectField = peergroupFiltersMap[filterName].type === 'select'
        const query = buildFindPeergroupQuery(state, form, filterName, value)

        if ((isSelectField || filterName === 'location') && !isEmpty(query)) {
          return of(findPeergroupActionCreator(query))
        }

        return empty()
      }))

const editRelationEpic: Epic = (action$, store) =>
  action$
    .pipe(byType(EDIT_RELATION)).pipe(
      mergeMap(({ payload: relationId }) => {
        const state = store.getState()
        const { peergroups, relations } = state.data.peergroups
        const relation = relations.byKey[relationId]
        const peergroup = peergroups.byKey[relation.peergroupId]

        const filters = getPeergroupFilterNames(peergroup)
        const filtersData = getFormPeerFiltersInitialValues(peergroup)

        const data = {
          name: relation.name,
          peergroup: filtersData,
        }

        const streams = [
          destroy(FORM_EDIT_CUSTOM_PEER_GROUP),
          hidePeergroupPopupActionCreator(relationId),
          setFoundPeergroupActionCreator(peergroup),
          showPeergroupEditFormActionCreator(relationId),
          initialize(FORM_EDIT_CUSTOM_PEER_GROUP, data, true),
          setPeergroupFiltersActionCreator(filters),
        ]

        return from(streams)
      }))

const editMemberPeergroupRelation: Epic = (action$) =>
  action$
    .pipe(formSubmitted(FORM_EDIT_CUSTOM_PEER_GROUP)).pipe(
      mergeMap(() => from([
      // hide form
        closePeergroupFormActionCreator(),
        reset(FORM_EDIT_CUSTOM_PEER_GROUP),
        resetPeergroupFiltersActionCreator(),
      ])))

const createMemberPeergroupRelation: Epic = (action$) =>
  action$
    .pipe(formSubmitted()).pipe(
      mergeMap(({ payload }) => {
        if (payload.formName === FORM_CREATE_CUSTOM_PEER_GROUP) {
          const rawRelation = payload.response as RawPeergroupRelation

          return from<Action[]>([
            appendPeergroupRelationActionCreator(payload.response),
            selectRelationActionCreator(rawRelation.id),
            closePeergroupFormActionCreator(),
            reset(FORM_CREATE_CUSTOM_PEER_GROUP),
            resetPeergroupFiltersActionCreator(),
          ])
        }
        return empty()
      }))

export default combineEpics(
  getPeergroupsEpic,
  getPeergroupTotalsEpic,
  randomizePeergroupEpic,
  editRelationEpic,
  handleChangePeergroupForm,
  deleteRelationEpic,
  findPeergroupEpic,
  createMemberPeergroupRelation,
  editMemberPeergroupRelation,
)
